import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import React from 'react';

interface InteractiveHeroProps {
  images: string[];
}

export const InteractiveHero = ({ images }: InteractiveHeroProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const autoplayInterval = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimeout = useRef<NodeJS.Timeout | null>(null);

  // Responsive: update width/height on resize
  useEffect(() => {
    function updateSize() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Autoplay logic
  useEffect(() => {
    if (isAutoplay) {
      autoplayInterval.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 2000); // Change image every 2 seconds
    } else if (autoplayInterval.current) {
      clearInterval(autoplayInterval.current);
      autoplayInterval.current = null;
    }
    return () => {
      if (autoplayInterval.current) {
        clearInterval(autoplayInterval.current);
        autoplayInterval.current = null;
      }
    };
  }, [isAutoplay, images.length]);

  // Helper to start inactivity timeout
  const startInactivityTimeout = () => {
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
    inactivityTimeout.current = setTimeout(() => {
      setIsAutoplay(true);
    }, 10000); // 10 seconds
  };

  // Mouse move handler
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newIndex = Math.min(
        Math.floor(x / (containerWidth / images.length)),
        images.length - 1
      );
      setCurrentImageIndex(newIndex);
      setHovered(true);
      if (isAutoplay) setIsAutoplay(false); // Stop autoplay on user interaction
      startInactivityTimeout(); // Restart inactivity timer
    }
  };

  // Mouse leave handler
  const handleMouseLeave = () => {
    setCurrentImageIndex(Math.floor(images.length / 2));
    setHovered(false);
    startInactivityTimeout(); // Restart inactivity timer
  };

  // Scroll detection for arrow visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollArrow(scrollTop < 50); // Show arrow when near top, hide when scrolled
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Clean up inactivity timeout on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
    };
  }, []);

  // Calculate the center x for each section (center of the 5px square)
  const sectionWidth = containerWidth / images.length;
  const sectionCenters = images.map((_, i) => sectionWidth * i + sectionWidth / 2);

  return (
    <div className="w-full pt-[50px] flex justify-center items-center">
      <div
        ref={containerRef}
        className="w-full h-[80vh] relative overflow-hidden cursor-ew-resize flex items-center justify-center"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Squares at the middle */}
        <div className="absolute left-0 w-full z-20 pointer-events-none" style={{ top: '50%', transform: 'translateY(-50%)' }}>
          {sectionCenters.map((center, i) => (
            <motion.div
              key={i}
              animate={{
                width: i === currentImageIndex ? 20 : 5,
                height: i === currentImageIndex ? 20 : 5,
                background: '#0048FF',
                borderRadius: 0,
                opacity: 1,
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 25,
              }}
              style={{
                position: 'absolute',
                left: `${((center) / containerWidth) * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 20,
                opacity: 1,
              }}
            />
          ))}
        </div>
        {/* Vertical blue line */}
        {containerWidth > 0 && hovered && (
          <div
            className="fixed top-0 z-10 pointer-events-none"
            style={{
              left: containerRef.current
                ? `${containerRef.current.getBoundingClientRect().left + sectionCenters[currentImageIndex] - 0.5}px`
                : 0,
              width: 1,
              background: '#0048FF',
              height: '100vh',
              transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)',
            }}
          />
        )}
        {/* Images */}
        {images.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              opacity: currentImageIndex === index ? 1 : 0,
              transition: 'opacity 0.1s ease-out'
            }}
          >
            <img
              src={image}
              alt={`Hero image ${index + 1}`}
              className="max-w-full max-h-full w-auto h-auto object-contain block mx-auto"
              style={{
                objectPosition: 'center center'
              }}
            />
          </div>
        ))}
      </div>
      
      {/* Animated Scroll Arrow */}
      {showScrollArrow && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5 }}
          className="fixed bottom-[40px] left-1/2 transform -translate-x-1/2 flex flex-col items-center z-50"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="flex flex-col items-center"
          >
            {/* Arrow line */}
            <div className="w-[1px] h-[24px] bg-[#0048FF]"></div>
            {/* Arrow head - simple triangle */}
            <div 
              className="w-0 h-0 border-l-[3px] border-r-[3px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#0048FF]"
              style={{ marginTop: '-1px' }}
            ></div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}; 